import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import CloudOffRounded from "@mui/icons-material/CloudOffRounded";
import QrCodeScannerRounded from "@mui/icons-material/QrCodeScannerRounded";
import { useScreenWakeLock } from "../../../common/hooks/deviceState";
import { KioskScanFrame, KioskStatusBar, KioskSurface } from "../components/KioskChrome";
import { KioskResultView } from "../components/KioskResultView";
import { KIOSK, KIOSK_FONT_HREF, kpx } from "../components/kioskTheme";
import { useKioskController } from "../hooks/useKioskController";
import { useQrCamera, type CameraFacing } from "../hooks/useQrCamera";
import { useResultTimeout } from "../hooks/useResultTimeout";
import { unlockKioskSound } from "../lib/chime";
import { KIOSK_PROBLEM_MS, KIOSK_RESULT_MS } from "../lib/kioskResult";
import { setupCodeFromHash } from "../lib/setupCode";

const FACING_KEY = "bw.kiosk.cameraFacing";

function readFacing(): CameraFacing {
  try {
    return window.localStorage.getItem(FACING_KEY) === "environment" ? "environment" : "user";
  } catch {
    return "user";
  }
}

function useKioskFont() {
  useEffect(() => {
    if (document.querySelector(`link[href="${KIOSK_FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = KIOSK_FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

function requestFullscreen() {
  unlockKioskSound();
  const root = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  try {
    if (document.fullscreenElement) return;
    if (root.requestFullscreen) void root.requestFullscreen().catch(() => undefined);
    else void root.webkitRequestFullscreen?.();
  } catch {
    // Full screen is a convenience; guided access / kiosk mode does the rest.
  }
}

const primaryButtonSx = {
  height: kpx(64),
  px: kpx(28),
  borderRadius: kpx(14),
  fontFamily: KIOSK.font,
  fontSize: kpx(22),
  fontWeight: 700,
  gap: 1.25,
  "&.Mui-disabled": { opacity: 0.5 },
} as const;

/** One-time setup: enter or scan the code from Front desk › Devices. */
function PairingView({
  notice,
  error,
  pairing,
  videoRef,
  cameraOn,
  onToggleCamera,
  cameraStatus,
  mirrored,
}: {
  notice: string | null;
  error: string | null;
  pairing: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraOn: boolean;
  onToggleCamera: () => void;
  cameraStatus: ReturnType<typeof useQrCamera>;
  mirrored: boolean;
}) {
  const noCamera = cameraOn && (cameraStatus === "blocked" || cameraStatus === "unavailable");
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: kpx(70),
        p: kpx(60),
        boxSizing: "border-box",
        "@media (orientation: portrait)": { flexDirection: "column", gap: kpx(48) },
      }}
    >
      <KioskScanFrame
        videoRef={videoRef}
        cameraStatus={cameraOn ? cameraStatus : "stopped"}
        mirrored={mirrored}
        checking={pairing}
        label="Camera for the kiosk setup code"
      />
      <Box sx={{ maxWidth: kpx(480), width: "100%" }}>
        <Box component="h1" sx={{ m: 0, fontSize: kpx(52), fontWeight: 700, lineHeight: 1.05 }}>
          Set up this kiosk
        </Box>
        <Box sx={{ fontSize: kpx(22), color: KIOSK.text2, mt: kpx(18), lineHeight: 1.4 }}>
          On the front desk computer, open Front desk › Devices › Start scan kiosk, then scan the
          code or open the link on this tablet.
        </Box>
        {notice ? (
          <Box role="status" sx={{ mt: kpx(20), fontSize: kpx(18), color: KIOSK.alert, lineHeight: 1.4 }}>
            {notice}
          </Box>
        ) : null}
        {error ? (
          <Box role="alert" sx={{ mt: kpx(20), fontSize: kpx(17), color: KIOSK.alert, lineHeight: 1.4 }}>
            {error}
          </Box>
        ) : null}
        {noCamera ? (
          <Box role="status" sx={{ mt: kpx(20), fontSize: kpx(18), color: KIOSK.alert, lineHeight: 1.4 }}>
            {cameraStatus === "blocked"
              ? "The camera is blocked for this page. "
              : "This tablet has no camera we can use. "}
            Use the link instead: on this tablet, sign in to Badminton Warriors Business, open Front
            desk › Devices › Start scan kiosk, and tap Open kiosk on this device.
          </Box>
        ) : null}
        <Box sx={{ display: "flex", gap: 1.5, mt: kpx(24), flexWrap: "wrap" }}>
          <ButtonBase
            type="button"
            onClick={onToggleCamera}
            disabled={pairing}
            sx={{
              ...primaryButtonSx,
              bgcolor: cameraOn ? "transparent" : KIOSK.green,
              color: KIOSK.white,
              border: cameraOn ? `2px solid ${KIOSK.line}` : "none",
            }}
          >
            {pairing ? <CircularProgress size={22} sx={{ color: "inherit" }} /> : <QrCodeScannerRounded />}
            {pairing ? "Pairing…" : cameraOn ? "Stop camera" : "Scan setup code"}
          </ButtonBase>
        </Box>
        <Box sx={{ fontSize: kpx(17), color: KIOSK.text3, mt: kpx(26), lineHeight: 1.45 }}>
          The kiosk gets its own sign-in, not yours. It never takes payment and can be revoked any
          time in Devices.
        </Box>
      </Box>
    </Box>
  );
}

function ReadyView({
  videoRef,
  cameraStatus,
  mirrored,
  checking,
  off,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraStatus: ReturnType<typeof useQrCamera>;
  mirrored: boolean;
  checking: boolean;
  off: boolean;
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: kpx(70),
        p: kpx(60),
        boxSizing: "border-box",
        "@media (orientation: portrait)": { flexDirection: "column", gap: kpx(56), textAlign: "center" },
      }}
    >
      <KioskScanFrame
        videoRef={videoRef}
        cameraStatus={off ? "stopped" : cameraStatus}
        mirrored={mirrored}
        checking={checking}
        label="Check-in scanner camera"
      />
      <Box sx={{ maxWidth: kpx(480) }}>
        {off ? (
          <>
            <Box component="h1" sx={{ m: 0, fontSize: kpx(52), fontWeight: 700, lineHeight: 1.05 }}>
              Check-in is off
            </Box>
            <Box sx={{ fontSize: kpx(22), color: KIOSK.text2, mt: kpx(18), lineHeight: 1.4 }}>
              Self check-in is turned off for this facility. Please see the front desk.
            </Box>
          </>
        ) : (
          <>
            <Box component="h1" sx={{ m: 0, fontSize: kpx(52), fontWeight: 700, lineHeight: 1.05 }}>
              Show your check-in QR
            </Box>
            <Box sx={{ fontSize: kpx(22), color: KIOSK.text2, mt: kpx(18), lineHeight: 1.4 }}>
              Open Badminton Warriors, tap Check in, choose what you are here for and
              hold your phone up to the camera.
            </Box>
            <Box sx={{ fontSize: kpx(17), color: KIOSK.text3, mt: kpx(26), lineHeight: 1.45 }}>
              Pay, or tap Redeem one on your pass, in the app first. This kiosk never
              takes payment. No app? See the front desk.
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

function CenteredMessage({ busy, title, detail }: { busy?: boolean; title: string; detail?: string }) {
  return (
    <Box
      role="status"
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
        p: 6,
      }}
    >
      {busy ? (
        <CircularProgress size={44} sx={{ color: KIOSK.accent }} />
      ) : (
        <CloudOffRounded sx={{ fontSize: kpx(110), color: KIOSK.text2 }} />
      )}
      <Box sx={{ fontSize: kpx(40), fontWeight: 700 }}>{title}</Box>
      {detail ? <Box sx={{ fontSize: kpx(22), color: KIOSK.text2, maxWidth: kpx(620) }}>{detail}</Box> : null}
    </Box>
  );
}

/**
 * The entrance scan kiosk (#2691): a chrome-less tablet page with no staff or
 * player login, hosted beside the tournament venue display. It pairs once
 * with a setup code (typed, scanned, or from a `#code=` link), then scans
 * players' check-in QR codes and shows green or red for about 2.5 s. It never
 * shows payment UI. A revoked or expired kiosk returns to setup.
 */
export function KioskPage() {
  // Read the one-time setup code from the link once, then scrub it from the
  // address bar and history before anything else can see it.
  const [initialSetupCode] = useState(() => setupCodeFromHash(window.location.hash));
  useLayoutEffect(() => {
    if (!window.location.hash) return;
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }, []);

  useKioskFont();
  const kiosk = useKioskController({ initialSetupCode });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [facing, setFacing] = useState<CameraFacing>(readFacing);
  const [pairingCamera, setPairingCamera] = useState(false);

  const ready = kiosk.phase === "ready";
  const off = kiosk.session?.mode === "off";
  const cameraActive = (ready && !off) || (kiosk.phase === "pairing" && pairingCamera);
  const cameraStatus = useQrCamera({
    videoRef,
    active: cameraActive,
    facing,
    // Pairing and scanning mount different <video> elements.
    restartKey: kiosk.phase,
    onDecode: kiosk.onDecode,
  });
  useScreenWakeLock(ready);

  useEffect(() => {
    if (kiosk.phase !== "pairing") setPairingCamera(false);
  }, [kiosk.phase]);

  // A setup code the camera read was refused: stop the camera until staff tap
  // Scan setup code again, so the same QR is not exchanged on every frame.
  useEffect(() => {
    if (kiosk.setupScanFailed) setPairingCamera(false);
  }, [kiosk.setupScanFailed]);

  const durationMs = kiosk.display?.kind === "problem" ? KIOSK_PROBLEM_MS : KIOSK_RESULT_MS;
  const secondsLeft = useResultTimeout(kiosk.display?.key ?? null, durationMs, kiosk.dismissResult);

  const switchCamera = useCallback(() => {
    setFacing((current) => {
      const next = current === "user" ? "environment" : "user";
      try {
        window.localStorage.setItem(FACING_KEY, next);
      } catch {
        // Remembering the camera is a convenience only.
      }
      return next;
    });
  }, []);

  const place = kiosk.session
    ? `${kiosk.session.facility_name} · ${kiosk.session.device_name}`
    : "Badminton Warriors · Check-in kiosk";
  const background =
    kiosk.display?.kind === "green" ? KIOSK.green : kiosk.display?.kind === "red" ? KIOSK.red : KIOSK.ready;

  return (
    <KioskSurface background={background} label="Check-in kiosk">
      <KioskStatusBar
        place={place}
        timeZone={kiosk.session?.facility_timezone}
        tone={kiosk.display && kiosk.display.kind !== "problem" ? "color" : "dark"}
        offline={kiosk.offline && kiosk.phase !== "unreachable"}
        onSwitchCamera={cameraActive && !kiosk.display ? switchCamera : undefined}
        onFullscreen={!kiosk.display ? requestFullscreen : undefined}
      />

      {kiosk.phase === "booting" ? <CenteredMessage busy title="Starting the kiosk…" /> : null}
      {kiosk.phase === "unreachable" ? (
        <CenteredMessage
          title="Can't reach Badminton Warriors"
          detail="Check this tablet's Wi-Fi. The kiosk keeps trying and starts by itself when it's back."
        />
      ) : null}
      {kiosk.phase === "pairing" ? (
        <PairingView
          notice={kiosk.pairingNotice}
          error={kiosk.pairingError}
          pairing={kiosk.pairing}
          videoRef={videoRef}
          cameraOn={pairingCamera}
          onToggleCamera={() => {
            if (pairingCamera) {
              setPairingCamera(false);
              return;
            }
            // A tap is the only retry: codes that failed are tried again now.
            requestFullscreen();
            kiosk.retrySetupScan();
            setPairingCamera(true);
          }}
          cameraStatus={cameraStatus}
          mirrored={facing === "user"}
        />
      ) : null}
      {ready ? (
        <ReadyView
          videoRef={videoRef}
          cameraStatus={cameraStatus}
          mirrored={facing === "user"}
          checking={kiosk.checking}
          off={off}
        />
      ) : null}
      {ready && kiosk.display ? (
        <KioskResultView
          display={kiosk.display}
          secondsLeft={secondsLeft}
          durationMs={durationMs}
          timeZone={kiosk.session?.facility_timezone}
        />
      ) : null}
    </KioskSurface>
  );
}
